export enum InvitationDirection {
  SENT = 'sent',
  RECEIVED = 'received',
}

export enum InvitationType {
  JOINT_ACCOUNT = 'joint_account',
}

export enum InvitationStatus {
  PENDING = 'pending',
  NOTIFIED = 'notified',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  EXPIRED = 'expired',
  PERSON_CREATED = 'person_created',
  CUSTOMER_CREATED = 'customer_created',
  PARTIAL = 'partial',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface InvitationParticipant {
  userId: string;
  displayName?: string;
  email: string;
  organisationId?: string;
  organisationName?: string;
}

/** An invitation may be sent before the invitee has a platform user ID. */
export interface InvitationInvitee {
  userId?: string;
  displayName?: string;
  email: string;
}

// Intentionally generic; extend per invitation type.
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface InvitationAttributes {}

export interface JointAccountInvitationAttributes extends InvitationAttributes {
  productCode: string;
  productName: string;
  /** Set only once acceptance has provisioned the shared account. */
  jointAccountOrganisationId?: string;
  /** The account the provisioned joint account holds, set alongside the organisation. */
  accountId?: string;
}

export interface BaseInvitation {
  id: string;
  type: InvitationType;
  status: InvitationStatus;
  inviter: InvitationParticipant;
  invitee: InvitationInvitee;
  expiresAt: string;
  sentAt: string;
  acceptedAt?: string;
  declinedAt?: string;
}

export interface JointAccountInvitation extends BaseInvitation {
  type: InvitationType.JOINT_ACCOUNT;
  attributes?: JointAccountInvitationAttributes;
}

export type Invitation = JointAccountInvitation;

export interface InvitationListResponse {
  result: Invitation[];
  page: number;
  size: number;
  total: number;
}

/* Customer-facing invitation API contracts (owned by org-metadata-service). */

export interface SendInvitationRequest {
  type: InvitationType;
  inviteeEmail: string;
  /** Required for JOINT_ACCOUNT invitations. */
  productCode: string;
}

export interface SendInvitationResponse {
  id: string;
  type: InvitationType;
  inviteeEmail: string;
  status: InvitationStatus;
  productCode?: string;
  expiresAt: string;
  createdAt: string;
}

export interface InvitationTokenRequest {
  token: string;
}

export interface VerifyInvitationResponse {
  id: string;
  type: InvitationType;
  inviteeEmail: string;
  productCode?: string;
  status: InvitationStatus;
  expiresAt: string;
  isUserExisting: boolean;
}

export interface SentInvitationSummary {
  id: string;
  type: InvitationType;
  inviteeEmail: string;
  status: InvitationStatus;
  productCode?: string;
  productName?: string;
  expiresAt: string;
  createdAt: string;
}

export interface SentInvitationListResponse {
  result: SentInvitationSummary[];
  page: number;
  size: number;
  total: number;
}

/* Internal joint-account contracts (org-metadata-service -> account-service). */

export interface ValidateJointAccountInvitationRequest {
  inviterUserId: string;
  inviteeUserId?: string;
  productCode: string;
}

export interface ValidateJointAccountInvitationResponse {
  productCode: string;
  productName: string;
}

export interface ProvisionJointAccountRequest {
  invitationId: string;
  /**
   * The joint account's Keycloak organisation, created by org-metadata-service
   * before provisioning so a retry provisions against the same organisation.
   */
  jointAccountOrganisationId: string;
  inviterOrganisationId: string;
  inviterUserId: string;
  inviteeOrganisationId: string;
  inviteeUserId: string;
  productCode: string;
}

export interface ProvisionJointAccountResponse {
  jointAccountOrganisationId: string;
  providerCustomerId: string;
  accountId?: string;
  /** The name the provider settled on, for the joint account's organisation record. */
  jointAccountName: string;
}

export enum InvitationEvent {
  SENT = 'sent',
  ACCEPTED = 'accepted',
  DECLINED = 'declined',
  EXPIRED = 'expired',
}

/**
 * Asks account-service to compose and deliver a joint-account invitation
 * notification. Account-service owns the Novu payloads, the product lookup and
 * the WEB_FRONTEND_URL the invitation link is built from.
 */
export interface NotifyJointAccountInvitationRequest {
  event: InvitationEvent;
  invitationId: string;
  inviterUserId: string;
  inviterOrgId: string;
  inviteeEmail: string;
  inviteeUserId?: string;
  productCode: string;
  /** Required for SENT: the link the invitee opens. */
  invitationToken?: string;
  /** Set once provisioning has completed, so the inviter is notified in the joint-account org. */
  jointAccountOrgId?: string;
}
